import { FormEvent, forwardRef, KeyboardEvent, useLayoutEffect, useRef } from "react"
import { Send } from "lucide-react"

const MAX_HEIGHT_PX = 120

interface AskDynamoComposerProps {
  dogName: string
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  disabled?: boolean
  sending?: boolean
}

export const AskDynamoComposer = forwardRef<HTMLTextAreaElement, AskDynamoComposerProps>(
  function AskDynamoComposer(
    { dogName, value, onChange, onSubmit, disabled = false, sending = false },
    ref,
  ) {
    const internalRef = useRef<HTMLTextAreaElement>(null)

    function setTextareaRef(element: HTMLTextAreaElement | null) {
      internalRef.current = element
      if (typeof ref === "function") {
        ref(element)
      } else if (ref) {
        ref.current = element
      }
    }

    useLayoutEffect(() => {
      const textarea = internalRef.current
      if (!textarea) return

      textarea.style.height = "auto"
      textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_HEIGHT_PX)}px`
    }, [value])

    function handleSubmit(event: FormEvent) {
      event.preventDefault()
      if (disabled || sending || !value.trim()) return
      onSubmit()
    }

    function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault()
        if (disabled || sending || !value.trim()) return
        onSubmit()
      }
    }

    return (
      <form onSubmit={handleSubmit} className="mt-4">
        <div className="flex items-end gap-2 rounded-2xl border border-[var(--color-divider)] bg-white px-3 py-2 shadow-sm">
          <textarea
            ref={setTextareaRef}
            rows={1}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Ask about ${dogName}'s behaviour…`}
            disabled={disabled || sending}
            className="min-h-9 min-w-0 flex-1 resize-none overflow-y-auto bg-transparent py-1.5 text-sm leading-normal text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-secondary)] disabled:opacity-60"
            style={{ maxHeight: MAX_HEIGHT_PX }}
            aria-label={`Ask about ${dogName}'s behaviour`}
          />
          <button
            type="submit"
            disabled={disabled || sending || !value.trim()}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-ask-dynamo)] text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label={sending ? "Sending" : "Send question"}
          >
            <Send size={16} aria-hidden />
          </button>
        </div>
      </form>
    )
  },
)
